if (!customElements.get('promo-slider')) {
  class PromoSlider extends HTMLElement {
    connectedCallback() {
      if (!this.track) this.init();
      if (this.slides.length < 2) return;

      document.addEventListener('visibilitychange', this.onVisibilityChange);
      document.addEventListener('shopify:block:select', this.onBlockSelect);
      document.addEventListener('shopify:block:deselect', this.onBlockDeselect);
      this.slides.forEach((slide) => this.observer.observe(slide));
      this.schedule();
    }

    disconnectedCallback() {
      if (this.slides.length < 2) return;
      window.clearTimeout(this.timer);
      this.observer.disconnect();
      document.removeEventListener('visibilitychange', this.onVisibilityChange);
      document.removeEventListener('shopify:block:select', this.onBlockSelect);
      document.removeEventListener('shopify:block:deselect', this.onBlockDeselect);
    }

    init() {
      this.track = this.querySelector('.promo-slider__track');
      this.slides = [...this.track.children].filter((el) => el.matches('.promo-slide'));
      this.prevButton = this.querySelector('.promo-slider__arrow--prev');
      this.nextButton = this.querySelector('.promo-slider__arrow--next');

      if (this.slides.length < 2) {
        this.prevButton.hidden = true;
        this.nextButton.hidden = true;
        return;
      }

      this.index = 0;
      this.pauses = new Set();
      this.reducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)');
      this.autoplay = this.dataset.autoplay === 'true' && !this.reducedMotion.matches;

      this.slides.forEach((slide, i) => {
        slide.setAttribute('aria-label', `${i + 1} ${this.dataset.of} ${this.slides.length}`);
      });

      this.onVisibilityChange = () => {
        if (document.hidden) {
          this.pause('hidden');
        } else {
          this.resume('hidden');
        }
      };
      this.onBlockSelect = this.onBlockSelect.bind(this);
      this.onBlockDeselect = this.onBlockDeselect.bind(this);
      this.observer = new IntersectionObserver(this.onIntersect.bind(this), {
        root: this.track,
        threshold: 0.6,
      });

      this.prevButton.addEventListener('click', () => this.goTo(this.index - 1, true));
      this.nextButton.addEventListener('click', () => this.goTo(this.index + 1, true));
      this.addEventListener('pointerenter', () => this.pause('hover'));
      this.addEventListener('pointerleave', () => this.resume('hover'));
      this.addEventListener('touchstart', () => this.pause('touch'), { passive: true });
      this.addEventListener('touchend', () => this.resume('touch'), { passive: true });
      this.addEventListener('focusin', () => this.pause('focus'));
      this.addEventListener('focusout', (event) => {
        if (!this.contains(event.relatedTarget)) this.resume('focus');
      });

      this.updateButtons();
    }

    goTo(index, fromUser = false) {
      const target = Math.min(Math.max(index, 0), this.slides.length - 1);
      this.track.scrollTo({
        left: target * this.track.clientWidth,
        behavior: this.reducedMotion.matches ? 'auto' : 'smooth',
      });
      this.setIndex(target);
      if (fromUser) this.schedule();
    }

    setIndex(index) {
      this.index = index;
      this.updateButtons();
    }

    updateButtons() {
      const atStart = this.index === 0;
      const atEnd = this.index === this.slides.length - 1;
      if ((atStart && document.activeElement === this.prevButton) || (atEnd && document.activeElement === this.nextButton)) {
        (atStart ? this.nextButton : this.prevButton).focus();
      }
      this.prevButton.disabled = atStart;
      this.nextButton.disabled = atEnd;
    }

    onIntersect(entries) {
      const visible = entries.find((entry) => entry.isIntersecting);
      if (visible) this.setIndex(this.slides.indexOf(visible.target));
    }

    schedule() {
      window.clearTimeout(this.timer);
      if (!this.autoplay || this.pauses.size) return;
      this.timer = window.setTimeout(() => {
        this.goTo((this.index + 1) % this.slides.length);
        this.schedule();
      }, Number(this.dataset.interval));
    }

    pause(reason) {
      this.pauses.add(reason);
      window.clearTimeout(this.timer);
    }

    resume(reason) {
      this.pauses.delete(reason);
      this.schedule();
    }

    onBlockSelect(event) {
      const index = this.slides.indexOf(event.target);
      if (index === -1) return;
      this.pause('editor');
      this.goTo(index);
    }

    onBlockDeselect(event) {
      if (this.slides.includes(event.target)) this.resume('editor');
    }
  }

  customElements.define('promo-slider', PromoSlider);
}
